import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import {useSelector} from 'react-redux';
import {agylePref} from '../../store/reducers/preferences';
import {useEffect, useState} from 'react';
import clsx from 'clsx';
import {getConfig} from '../../util/configUtil';
import APIHealthService from '../../app/apiHealthService';
import IconButton from '@mui/material/IconButton';
import {Box} from '@mui/material';
import Draggable from 'react-draggable';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';

const useStyles = makeStyles((theme) => ({
        root: {
            position: 'absolute',
            bottom: 20,
            right: 20,
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            backgroundColor: theme.palette.designSystem.colors.amber[750],
            border: `solid 2px ${theme.palette.mode === 'light' ? '#202020' : '#ffffff'}`,
            borderRadius: 8,
            padding: 10,
            userSelect: 'none',
            '&.left': {
                right: 'auto',
                left: 20,
            },
        },
    }),
);

const PasConnectionChecker = () => {
    const classes = useStyles();
    const agylePreference = useSelector(agylePref);

    const [position, setPosition] = useState('right');
    const [showAlert, setShowAlert] = useState(false);
    const [hide, setHide] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // Config
    const pasCheckInterval = getConfig('modules.pasHealthCheck.checkInterval', 60000);

    /**
     * Updates the `position` state based on the `snackbar` preferences from `agylePreference`.
     * If the snackbar is positioned at the bottom-right, the position is set to 'left';
     * otherwise it defaults to 'right'.
     *
     * Dependencies:
     * - `agylePreference?.snackbar`: The snackbar configuration object.
     */
    useEffect(() => {
        if (agylePreference?.snackbar) {
            setPosition((agylePreference.snackbar.vertical === 'bottom' && agylePreference.snackbar.horizontal === 'right')
                ? 'left'
                : 'right',
            );
        }
    }, [agylePreference?.snackbar]);

    /**
     * useEffect hook to periodically check the PAS connection status.
     *
     * - Calls the API Health Service method to check the connection status.
     * - Updates the `showAlert` state based on the response.
     * - Sets up an interval to repeat the check at the configured interval (`pasCheckInterval`).
     * - Cleans up the interval on a component unmount.
     */
    useEffect(() => {
        const checkConnection = async () => {
            const response = await APIHealthService.getHl7PasStatus();
            setShowAlert(!response?.check);
        };

        checkConnection();
        const intervalId = setInterval(checkConnection, pasCheckInterval);
        return () => clearInterval(intervalId);
    }, []);

    const handleDrag = (state) => (_, data) => {
        if (state) {
            setIsDragging(true);
        } else {
            setTimeout(() => {
                setIsDragging(false);
            }, 100);
        }
    };

    const handleHide = (state) => () => {
        if (isDragging) return;
        setHide(state);
    };

    if (!showAlert) return null;

    return (
        <>
            {!hide && (
                <div className={clsx(classes.root, position === 'left' && 'left')}>
                    <Typography variant="body1" color="white" fontWeight={600}>
                        3rd party connectivity issue identified. Ensure all patient transactions are validated.
                    </Typography>
                    <Box>
                        <IconButton size="small" onClick={handleHide(true)}>
                            {position === 'left' ? <KeyboardArrowLeftIcon/> : <KeyboardArrowRightIcon/>}
                        </IconButton>
                    </Box>
                </div>
            )}

            {hide && (
                <Draggable
                    onDrag={handleDrag(true)}
                    onStop={handleDrag(false)}
                    bounds={{
                        left: -window.innerWidth + 100,
                        top: -window.innerHeight + 100,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    <div className={clsx(classes.root, position === 'left' && 'left')}>
                        <IconButton size="small" onClick={handleHide(false)}>
                            <ReportGmailerrorredIcon/>
                        </IconButton>
                    </div>
                </Draggable>
            )}
        </>

    );
};

export default PasConnectionChecker;